import React, { useEffect, useState, Children } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './i18n';
import { HomePage } from './pages/HomePage';
import { ServicesPage } from './pages/ServicesPage';
import { ServiceDetailPage } from './pages/ServiceDetailPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { PricesPage } from './pages/PricesPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { PatientPrivacyNoticePage } from './pages/PatientPrivacyNoticePage';
import { TermsAndConditionsPage } from './pages/TermsAndConditionsPage';
import { BusinessCardPage } from './pages/BusinessCardPage';
import { BusinessCardNewPage } from './pages/BusinessCardNewPage';
import { BusinessCardNewUaPage } from './pages/BusinessCardNewUaPage';
import { BusinessCardNewRuPage } from './pages/BusinessCardNewRuPage';
import { UkrainianDentistPage } from './pages/UkrainianDentistPage';
import { ImplantyLondonPage } from './pages/ImplantyLondonPage';
import { VeneersLondonPage } from './pages/VeneersLondonPage';
import { RootCanalLondonPage } from './pages/RootCanalLondonPage';
import { EmergencyDentistLondonPage } from './pages/EmergencyDentistLondonPage';
import { RussianDentistPage } from './pages/RussianDentistPage';
import { ImplantyLondonRuPage } from './pages/ImplantyLondonRuPage';
import { VeneersLondonRuPage } from './pages/VeneersLondonRuPage';
import { RootCanalLondonRuPage } from './pages/RootCanalLondonRuPage';
import { EmergencyDentistLondonRuPage } from './pages/EmergencyDentistLondonRuPage';
import { BlogIndexPage } from './pages/blog/BlogIndexPage';
import { ImplantyCostArticle } from './pages/blog/ImplantyCostArticle';
import { NhsVsPrivateArticle } from './pages/blog/NhsVsPrivateArticle';
import { WithoutInsuranceArticle } from './pages/blog/WithoutInsuranceArticle';
import { ToothPainArticle } from './pages/blog/ToothPainArticle';
import { VeneersGuideArticle } from './pages/blog/VeneersGuideArticle';
import { ChooseDentistArticle } from './pages/blog/ChooseDentistArticle';
import { SitemapPage } from './pages/SitemapPage';
import { OrthodonticsPage } from './pages/OrthodonticsPage';
import { ClearAlignersPage } from './pages/ClearAlignersPage';
import { InvisalignAlignersPage } from './pages/InvisalignAlignersPage';
import { MetalBracesPage } from './pages/MetalBracesPage';
import { CeramicBracesPage } from './pages/CeramicBracesPage';
import { RetainersPage } from './pages/RetainersPage';
import { ChildrensOrthodonticsPage } from './pages/ChildrensOrthodonticsPage';
import { GiftCertificate } from './components/GiftCertificate';
import { PrintPricePage } from './pages/PrintPricePage';
import { InfluencerInvitePage } from './pages/InfluencerInvitePage';
import { RaffleVoucherPage } from './pages/RaffleVoucherPage';
import { LogoPage } from './pages/LogoPage';
import { OrthodonticFlyerUaPage } from './pages/OrthodonticFlyerUaPage';
import { PaediatricDentistryPage } from './pages/PaediatricDentistryPage';
import { WhatsAppQrPage } from './pages/WhatsAppQrPage';
import { PatientExperiencePage } from './pages/PatientExperiencePage';
import { ResultsPage } from './pages/ResultsPage';
import { ReviewsPage } from './pages/ReviewsPage';
import { AiSupportPage } from './pages/AiSupportPage';
// ── Global scroll-to-top on every route change ──
// Exception: /prices#anchor — let PricesPage handle its own hash scrolling
function ScrollToTop() {
  const {
    pathname,
    hash
  } = useLocation();
  useEffect(() => {
    // Disable browser scroll restoration globally
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);
  useEffect(() => {
    // If navigating to /prices with a hash, skip — PricesPage handles it
    const isPricesWithHash = pathname.includes('/prices') && hash;
    if (isPricesWithHash) return;
    // Immediate scroll
    window.scrollTo(0, 0);
    // Reliable delayed scroll for dynamic content / mobile
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
    const t = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return null;
}
// Detect user's preferred language from browser
function detectUserLanguage(): 'en' | 'ua' | 'ru' | 'ro' {
  const browserLangs = navigator.languages || [navigator.language];
  for (const lang of browserLangs) {
    const langCode = lang.toLowerCase().split('-')[0];
    if (langCode === 'uk' || langCode === 'ua') {
      return 'ua';
    }
    if (langCode === 'ru') {
      return 'ru';
    }
    if (langCode === 'ro') {
      return 'ro';
    }
    if (langCode === 'en') {
      return 'en';
    }
  }
  return 'en';
}
function getStoredLanguage(): 'en' | 'ua' | 'ru' | 'ro' | null {
  try {
    const stored = localStorage.getItem('dentico-preferred-lang');
    if (stored === 'en' || stored === 'ua' || stored === 'ru' || stored === 'ro') {
      return stored as 'en' | 'ua' | 'ru' | 'ro';
    }
  } catch {
    // localStorage not available
  }
  return null;
}
function setStoredLanguage(lang: 'en' | 'ua' | 'ru' | 'ro') {
  try {
    localStorage.setItem('dentico-preferred-lang', lang);
  } catch {
    // localStorage not available
  }
}
function LanguageRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const storedLang = getStoredLanguage();
    if (storedLang) {
      navigate(`/${storedLang}`, {
        replace: true
      });
      return;
    }
    const detectedLang = detectUserLanguage();
    setStoredLanguage(detectedLang);
    navigate(`/${detectedLang}`, {
      replace: true
    });
  }, [navigate]);
  return null;
}
function LanguageRoutes() {
  const {
    lang
  } = useParams();
  const validLangs = ['en', 'ua', 'ru', 'ro'];
  if (!validLangs.includes(lang || '')) {
    return <Navigate to="/en" replace data-id="element-0" data-mp="21rkgzrkbsaap" />;
  }
  useEffect(() => {
    if (lang && validLangs.includes(lang)) {
      setStoredLanguage(lang as 'en' | 'ua' | 'ru' | 'ro');
    }
  }, [lang]);
  return <LanguageProvider data-id="element-1" data-mp="1wuu2v9s2zorx">
      <Routes data-id="element-2" data-mp="2as0emkee0n7n">
        <Route index element={<HomePage data-id="element-4" data-mp="2at1wb7pws7yc" />} data-id="element-3" data-mp="2asik87mxwds8" />
        <Route path="ukrainskyi-stomatolog-london" element={<UkrainianDentistPage data-id="element-6" data-mp="25se5am1ojr44" />} data-id="element-5" data-mp="2aq5tp6348v3k" />
        <Route path="implanty-london" element={<ImplantyLondonPage data-id="element-8" data-mp="2kpn2hwdnzp3r" />} data-id="element-7" data-mp="2kqg9mygjcgmf" />
        <Route path="veneers-london" element={<VeneersLondonPage data-id="element-10" data-mp="2frlfx1h157nj" />} data-id="element-9" data-mp="2kolopk59koic" />
        <Route path="root-canal-london" element={<RootCanalLondonPage data-id="element-12" data-mp="2fp912x6kjdep" />} data-id="element-11" data-mp="2fqafnbmsmi16" />
        <Route path="emergency-dentist-london" element={<EmergencyDentistLondonPage data-id="element-14" data-mp="2tkkvf3gtb1v3" />} data-id="element-13" data-mp="2tj9s14ra3w4i" />
        <Route path="russkij-stomatolog-london" element={<RussianDentistPage data-id="element-16" data-mp="33go8h4d7ckwt" />} data-id="element-15" data-mp="2pi7pady5l1xs" />
        <Route path="implantaciya-london" element={<ImplantyLondonRuPage data-id="element-18" data-mp="33el5zkuv4zb7" />} data-id="element-17" data-mp="33hplheds3hh6" />
        <Route path="viniry-london" element={<VeneersLondonRuPage data-id="element-20" data-mp="2yjo18q4m9c44" />} data-id="element-19" data-mp="2yimevpi5h373" />
        <Route path="lechenie-kanalov-london" element={<RootCanalLondonRuPage data-id="element-22" data-mp="3i4efx1t6b4gp" />} data-id="element-21" data-mp="3i3mv8m146r57" />
        <Route path="neotlozhnaya-stomatologiya-london" element={<EmergencyDentistLondonRuPage data-id="element-24" data-mp="3n0dbpagtzor5" />} data-id="element-23" data-mp="3n39af10i6hgn" />
        <Route path="blog" element={<BlogIndexPage data-id="element-26" data-mp="38571scem75sj" />} data-id="element-25" data-mp="384nlt18iyrh5" />
        <Route path="blog/dental-implants-cost-london" element={<ImplantyCostArticle data-id="element-28" data-mp="3d6mp6b49loe3" />} data-id="element-27" data-mp="3d4jtpbkcf31z" />
        <Route path="blog/nhs-vs-private-dentistry-uk" element={<NhsVsPrivateArticle data-id="element-30" data-mp="343vdca7h8qlx" />} data-id="element-29" data-mp="2zclr7jl505es" />
        <Route path="blog/dental-treatment-uk-without-insurance" element={<WithoutInsuranceArticle data-id="element-32" data-mp="2pfqsazkyc2zz" />} data-id="element-31" data-mp="344wwz32k6lf9" />
        <Route path="blog/toothache-london-what-to-do" element={<ToothPainArticle data-id="element-34" data-mp="2uetu8b5mxt3q" />} data-id="element-33" data-mp="2pcut77xdb89y" />
        <Route path="blog/veneers-london-everything-you-need-to-know" element={<VeneersGuideArticle data-id="element-36" data-mp="2ggn6voybbbib" />} data-id="element-35" data-mp="2ubpovagforbh" />
        <Route path="blog/how-to-choose-dentist-angel" element={<ChooseDentistArticle data-id="element-38" data-mp="30v3sihiqsjws" />} data-id="element-37" data-mp="2kg4f2q54imr6" />
        <Route path="sitemap" element={<SitemapPage data-id="element-40" data-mp="30tau1pia95zv" />} data-id="element-39" data-mp="30u2imgdgq3gn" />
        <Route path="services" element={<ServicesPage data-id="element-42" data-mp="2vxc2jt1nr7v8" />} data-id="element-41" data-mp="2vwk6kh45f84f" />
        <Route path="services/paediatric-dentistry" element={<PaediatricDentistryPage data-id="element-44" data-mp="2qw64dfp8aseb" />} data-id="element-43" data-mp="2vx289t9kyejq" />
        <Route path="services/orthodontics" element={<OrthodonticsPage data-id="element-46" data-mp="2lx31a0st6w8z" />} data-id="element-45" data-mp="2qz0spibd4xd0" />
        <Route path="services/orthodontics/clear-aligners" element={<ClearAlignersPage data-id="element-48" data-mp="2hxazq81u1qd4" />} data-id="element-47" data-mp="2lxuz7i9vp62f" />
        <Route path="services/orthodontics/invisalign-style-aligners" element={<InvisalignAlignersPage data-id="element-50" data-mp="2d3q0ydsf0scv" />} data-id="element-49" data-mp="2hycfgptsn116" />
        <Route path="services/orthodontics/metal-braces" element={<MetalBracesPage data-id="element-52" data-mp="237vc30xlm11n" />} data-id="element-51" data-mp="285wwpqaoa8f8" />
        <Route path="services/orthodontics/ceramic-braces" element={<CeramicBracesPage data-id="element-54" data-mp="1y8s5hc1yzmrk" />} data-id="element-53" data-mp="238y1t5j6pxfz" />
        <Route path="services/orthodontics/retainers" element={<RetainersPage data-id="element-56" data-mp="1u7zzqssb4001" />} data-id="element-55" data-mp="1y7qsv35b2s7q" />
        <Route path="services/orthodontics/children" element={<ChildrensOrthodonticsPage data-id="element-58" data-mp="9ocwywx27m91" />} data-id="element-57" data-mp="1u91iljfkdqsb" />
        <Route path="services/:serviceId" element={<ServiceDetailPage data-id="element-60" data-mp="dp6ixkqxyqza" />} data-id="element-59" data-mp="dq7wpwzcdrkp" />
        <Route path="prices" element={<PricesPage data-id="element-62" data-mp="ipucoe6k1lx7" />} data-id="element-61" data-mp="dskkwrvp0y67" />
        <Route path="price-list" element={<PrintPricePage data-id="element-64" data-mp="iqdvdx414eca" />} data-id="element-63" data-mp="iqm89p05jjnh" />
        <Route path="ai-support" element={<AiSupportPage data-id="element-66" data-mp="nnmebzx04mel" />} data-id="element-65" data-mp="nofay828xvj0" />
        <Route path="about" element={<AboutPage data-id="element-68" data-mp="3m6b5y763rob5" />} data-id="element-67" data-mp="nnw2rj2eeli6" />
        <Route path="patient-experience" element={<PatientExperiencePage data-id="element-70" data-mp="3m84cznkmoajo" />} data-id="element-69" data-mp="3m6t79i7mgspx" />
        <Route path="results" element={<ResultsPage data-id="element-72" data-mp="3r6fn7zeu4n2f" />} data-id="element-71" data-mp="3r5e1n1070i6g" />
        <Route path="reviews" element={<ReviewsPage data-id="element-74" data-mp="3r31n6xtn8py5" />} data-id="element-73" data-mp="3r5xl4m5hreml" />
        <Route path="contact" element={<ContactPage data-id="element-76" data-mp="3w3pbvcuegup7" />} data-id="element-75" data-mp="3w3yzwuvvwrs9" />
        <Route path="privacy-policy" element={<PrivacyPolicyPage data-id="element-78" data-mp="4qbfuhj71x07" />} data-id="element-77" data-mp="3w2e657he9cvg" />
        <Route path="cookie-policy" element={<CookiePolicyPage data-id="element-80" data-mp="4rmjmhcn34rb" />} data-id="element-79" data-mp="4ql2pw8xzo1o" />
        <Route path="patient-privacy-notice" element={<PatientPrivacyNoticePage data-id="element-82" data-mp="33dz37jbjel0h" />} data-id="element-81" data-mp="33gbgvka9i97q" />
        <Route path="terms-and-conditions" element={<TermsAndConditionsPage data-id="element-84" data-mp="2f7582vrmnxc1" />} data-id="element-83" data-mp="38duxgqufmy2u" />
        <Route path="*" element={<Navigate to={`/${lang}`} replace data-id="element-86" data-mp="2f6vdeuvn1200" />} data-id="element-85" data-mp="2f6bzdp93yxr9" />
      </Routes>
    </LanguageProvider>;
}
export function App() {
  return <BrowserRouter data-id="element-87" data-mp="2p5b817tvite9">
      <ScrollToTop data-id="element-88" data-mp="2p4rstyvlyj3x" />
      <Routes data-id="element-89" data-mp="2p6ciba32fbux">
        <Route path="/business-card" element={<BusinessCardPage data-id="element-91" data-mp="2u4e1rw4eld6x" />} data-id="element-90" data-mp="2p5uf1thzjxdi" />
        <Route path="/business-card-new" element={<BusinessCardNewPage data-id="element-93" data-mp="2u3mgbe4isvud" />} data-id="element-92" data-mp="2u4nw1vwhe6if" />
        <Route path="/business-card-new-ua" element={<BusinessCardNewUaPage data-id="element-95" data-mp="1wej4fpahb2gw" />} data-id="element-94" data-mp="1wgvxaxhryx8q" />
        <Route path="/business-card-new-ru" element={<BusinessCardNewRuPage data-id="element-97" data-mp="21ddxrl62o9f9" />} data-id="element-96" data-mp="21f76r745r5qf" />
        <Route path="/gift-certificate" element={<GiftCertificate data-id="element-99" data-mp="26dicp6rc0w3d" />} data-id="element-98" data-mp="26ejoxek33omo" />
        <Route path="/influencer-invite" element={<InfluencerInvitePage data-id="element-101" data-mp="2b6blc5px8okq" />} data-id="element-100" data-mp="2b59w8xc2m1k0" />
        <Route path="/raffle-voucher" element={<RaffleVoucherPage data-id="element-103" data-mp="1dqnukghzm8t2" />} data-id="element-102" data-mp="2b7mkfs8f985i" />
        <Route path="/whatsapp-qr" element={<WhatsAppQrPage data-id="element-105" data-mp="1dobfcb3m6cjp" />} data-id="element-104" data-mp="1dpml2ggmdudg" />
        <Route path="/logo" element={<LogoPage data-id="element-107" data-mp="1inxskkjfzssi" />} data-id="element-106" data-mp="1iopipfu9sybe" />
        <Route path="/orthodontic-flyer-ua" element={<OrthodonticFlyerUaPage data-id="element-109" data-mp="3vyh2ninoomzt" />} data-id="element-108" data-mp="1in4lfigkn19u" />
        <Route path="/" element={<LanguageRedirect data-id="element-111" data-mp="3rxdqb64uh93c" />} data-id="element-110" data-mp="3vx617phpnrbv" />
        <Route path="/:lang/*" element={<LanguageRoutes data-id="element-113" data-mp="3rvsu7c2vti3d" />} data-id="element-112" data-mp="3rz8d6pzofba2" />
        <Route path="*" element={<LanguageRedirect data-id="element-115" data-mp="3mxrcovl3tqu0" />} data-id="element-114" data-mp="3mykc1bhqhe24" />
      </Routes>
    </BrowserRouter>;
}